import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SearchIcon } from "lucide-react";
import ButtonsHeader from "@/app/components/common/ButtonsHeader";
import OrdersSkeleton, {
  OrderCardSkeleton,
} from "@/app/components/common/skeletons/OrdersSkeleton";
import ConfirmDialog from "@/app/components/common/ConfirmAlert";
import CustomImage from "@/app/components/common/Image";
import { ProductReviewForm } from "@/app/components/cart/ProductReview";
import OrderCard from "@/app/components/orders/OrderCard";
import PickupOrderOtpDialog from "@/app/components/orders/PickupOrderOtpDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";
import { useAuth } from "@/contexts/AuthContext";
import { copyToClipboard, hapticNotification } from "@/lib/capacitor";
import { useConfirm } from "@/hooks/use-confirm";
import { footerBg3Style } from "@/assets";
import {
  useGetCouriersQuery,
  useUpdateOrderStatusMutation,
} from "@/lib/api/apiSlice";
import { inter } from "@/lib/fonts";
import { useShowInitialLoader } from "@/lib/hooks";
import { useRtkInfiniteList } from "@/lib/hooks/useRtkInfiniteList";
import {
  defaultPaginationOptions,
  endpoints,
  IOrder,
  OrderDeliveryType,
  OrderStatus,
  SEARCH_DEBOUNCE_DELAY,
  UserType,
} from "muminsmart-utils";
import { toast } from "sonner";

const ORDER_RELATIONS = [
  "items",
  "items.productVariant",
  "items.productVariant.product",
  "items.productVariant.variantAttributes",
  "invoices",
  "address",
  "user",
].join(",");

export default function Page() {
  const { data: session, status } = useAuth();
  const { isOpen, confirm, handleCancel, handleConfirm, title, description } =
    useConfirm();

  const isSeller = useMemo(
    () => session?.user?.type === UserType.Seller,
    [session],
  );

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | null>(
    OrderStatus.Processing,
  );
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [updatingOrderStatusId, setUpdatingOrderStatusId] = useState<
    string | null
  >(null);
  const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
  const [pickupOtpOrder, setPickupOtpOrder] = useState<IOrder | null>(null);
  const [reviewPromptOrder, setReviewPromptOrder] = useState<IOrder | null>(
    null,
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const [updateOrderStatusMutation] = useUpdateOrderStatusMutation();
  const { data: couriers = [] } = useGetCouriersQuery(undefined, {
    skip: !isSeller,
  });

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, SEARCH_DEBOUNCE_DELAY);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchInput]);

  const baseParams = useMemo(
    () => ({
      ...defaultPaginationOptions,
      search: searchTerm,
      sortBy: "createdAt",
      sortOrder: "DESC",
      status: selectedStatus || undefined,
      relations: ORDER_RELATIONS,
    }),
    [searchTerm, selectedStatus],
  );

  const ordersEndpoint = isSeller
    ? endpoints.order.sellerOrders
    : endpoints.order.myOrders;

  const {
    items: orders,
    page,
    hasMore,
    isInitialLoading,
    isLoadingMore,
    loadPage,
    replaceItem,
    patchItem,
    totalItems,
  } = useRtkInfiniteList<IOrder>({
    endpoint: ordersEndpoint,
    baseParams,
    skip: status !== "authenticated",
    errorMessage: "Failed to fetch orders",
  });

  const ordersData = isInitialLoading ? undefined : orders;
  const showOrdersLoader = useShowInitialLoader(isInitialLoading, ordersData);

  useEffect(() => {
    void loadPage(1, true);
  }, [loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          hasMore &&
          !isLoadingMore &&
          !isInitialLoading
        ) {
          void loadPage(page + 1);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isInitialLoading, isLoadingMore, loadPage, page]);

  const toggleExpand = useCallback((orderId: string) => {
    setExpandedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  }, []);

  const copyAddress = useCallback(async (orderId: string, address: string) => {
    const copied = await copyToClipboard(address);
    if (!copied) {
      return;
    }

    await hapticNotification();
    setCopiedOrderId(orderId);
    setTimeout(() => setCopiedOrderId(null), 2000);
  }, []);

  const applyOrderStatusUpdate = useCallback(
    async (orderId: string, nextStatus: OrderStatus) => {
      setUpdatingOrderStatusId(orderId);
      try {
        const updatedOrder = await updateOrderStatusMutation({
          id: orderId,
          status: nextStatus,
        }).unwrap();

        patchItem(orderId, { status: updatedOrder.status });
        toast.success("Order status updated");
        return updatedOrder;
      } catch {
        toast.error("Failed to update order status");
        return null;
      } finally {
        setUpdatingOrderStatusId(null);
      }
    },
    [patchItem, updateOrderStatusMutation],
  );

  const updateOrderStatus = useCallback(
    async (orderId: string, nextStatus: OrderStatus) => {
      if (updatingOrderStatusId) {
        return;
      }

      const order = orders.find((item) => item.id === orderId);

      // Buyers may only mark their own Shipped order as received (Delivered).
      if (!isSeller) {
        if (
          nextStatus !== OrderStatus.Delivered ||
          order?.status !== OrderStatus.Shipped
        ) {
          return;
        }

        const confirmed = await confirm({
          title: "Mark Order as Received",
          description:
            "Are you sure you have received this order? This action cannot be undone.",
        });

        if (!confirmed) {
          return;
        }

        const updatedOrder = await applyOrderStatusUpdate(orderId, nextStatus);
        if (updatedOrder) {
          setReviewPromptOrder({ ...order, status: updatedOrder.status });
        }
        return;
      }

      if (
        nextStatus === OrderStatus.Cancelled ||
        nextStatus === OrderStatus.Returned
      ) {
        const confirmed = await confirm({
          title: `Mark Order as ${nextStatus}`,
          description: `Are you sure you want to mark this order as ${nextStatus}? This action cannot be undone.`,
        });

        if (!confirmed) {
          return;
        }
      }

      if (
        nextStatus === OrderStatus.Delivered &&
        order?.deliveryType === OrderDeliveryType.Pickup
      ) {
        setPickupOtpOrder(order);
        return;
      }

      await applyOrderStatusUpdate(orderId, nextStatus);
    },
    [applyOrderStatusUpdate, confirm, isSeller, orders, updatingOrderStatusId],
  );

  const reviewPromptItems = useMemo(() => {
    const productItems = new Map<
      string,
      NonNullable<IOrder["items"][number]["productVariant"]>
    >();

    for (const item of reviewPromptOrder?.items ?? []) {
      const variant = item.productVariant;
      const productId = variant?.product?.id;

      if (!variant || !productId || productItems.has(productId)) {
        continue;
      }

      productItems.set(productId, variant);
    }

    return Array.from(productItems.values());
  }, [reviewPromptOrder]);

  const handlePickupOtpVerified = useCallback(async () => {
    if (!pickupOtpOrder) {
      return;
    }

    const orderId = pickupOtpOrder.id;
    setUpdatingOrderStatusId(orderId);
    try {
      const updatedOrder = await updateOrderStatusMutation({
        id: orderId,
        status: OrderStatus.Delivered,
      }).unwrap();

      patchItem(orderId, { status: updatedOrder.status });
      toast.success("Order marked as delivered");
      setPickupOtpOrder(null);
    } catch {
      toast.error("Failed to update order status");
    } finally {
      setUpdatingOrderStatusId(null);
    }
  }, [patchItem, pickupOtpOrder, updateOrderStatusMutation]);

  const handleOrderUpdated = useCallback(
    (updatedOrder: IOrder) => {
      replaceItem(updatedOrder);
    },
    [replaceItem],
  );

  let ordersContent: React.ReactNode;

  if (showOrdersLoader) {
    ordersContent = <OrdersSkeleton />;
  } else if (orders.length === 0) {
    ordersContent = (
      <div className="py-16 text-center text-sm text-gray-500">
        No {selectedStatus} orders found.
      </div>
    );
  } else {
    ordersContent = orders.map((order, index) => (
      <OrderCard
        key={order.id}
        order={order}
        index={index}
        isSeller={isSeller}
        isExpanded={expandedOrderIds.has(order.id)}
        couriers={couriers}
        copiedOrderId={copiedOrderId}
        updatingOrderStatusId={updatingOrderStatusId}
        onToggleExpand={toggleExpand}
        onCopyAddress={copyAddress}
        onUpdateStatus={updateOrderStatus}
        onOrderUpdated={handleOrderUpdated}
      />
    ));
  }

  return (
    <div className="min-h-screen">
      <ButtonsHeader heading="My Orders" />
      <div
        className="mt-4 min-h-[calc(100vh-5rem)] rounded-t-3xl bg-white py-6"
        style={footerBg3Style}
      >
        <div className="px-4 pb-4">
          <InputGroup className="w-full rounded-full border-none bg-white shadow-sm">
            <InputGroupInput
              type="text"
              placeholder="Search..."
              value={searchInput}
              onChange={(event) => {
                setSearchInput(event.target.value);
              }}
            />
            <InputGroupAddon>
              <SearchIcon className="h-4 w-4 text-gray-400" />
            </InputGroupAddon>
          </InputGroup>
        </div>

        <div className="no-scrollbar flex items-center gap-2 overflow-x-auto px-4 pb-4">
          {Object.values(OrderStatus).map((orderStatus) => {
            const isActive = orderStatus === selectedStatus;

            return (
              <button
                key={orderStatus}
                type="button"
                onClick={() => {
                  if (orderStatus !== selectedStatus) {
                    setSelectedStatus(orderStatus);
                  } else {
                    setSelectedStatus(null);
                  }
                }}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm font-medium transition ${inter.className} ${
                  isActive
                    ? "border-primary bg-primary text-white"
                    : "border-zinc-300 bg-white text-zinc-700"
                }`}
              >
                {orderStatus}
                {isActive ? <span className="ml-1">({totalItems})</span> : null}
              </button>
            );
          })}
        </div>

        <div className="space-y-3 px-4 pt-3">
          {ordersContent}

          {isLoadingMore && !showOrdersLoader ? <OrderCardSkeleton /> : null}

          <div
            ref={sentinelRef}
            className="h-1 w-full py-4"
            aria-hidden="true"
          />
        </div>
      </div>
      <ConfirmDialog
        isOpen={isOpen}
        title={title}
        description={description}
        confirmButtonLabel="Confirm"
        cancelButtonLabel="Cancel"
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
      <PickupOrderOtpDialog
        order={pickupOtpOrder}
        isSubmitting={Boolean(
          pickupOtpOrder?.id && updatingOrderStatusId === pickupOtpOrder.id,
        )}
        onClose={() => setPickupOtpOrder(null)}
        onVerified={() => void handlePickupOtpVerified()}
      />
      <Dialog
        open={Boolean(reviewPromptOrder)}
        onOpenChange={(open) => {
          if (!open) {
            setReviewPromptOrder(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] gap-3 overflow-hidden rounded-3xl p-4">
          <DialogHeader className="gap-1 pr-6">
            <DialogTitle className="text-base">
              Review your products
            </DialogTitle>
            <DialogDescription className="text-xs">
              Your order was marked as delivered. Share your experience to help
              other shoppers.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[72vh] space-y-2 overflow-y-auto pr-1">
            {reviewPromptItems.map((variant) => {
              const product = variant.product;

              if (!product) {
                return null;
              }

              return (
                <div
                  key={product.id}
                  className="space-y-2 rounded-xl border border-gray-200 bg-white p-2"
                >
                  <div className="flex items-center gap-2">
                    <CustomImage
                      src={variant.featuredImage ?? ""}
                      alt={product.name}
                      className="h-10 w-10 rounded-lg object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-1 text-xs font-semibold text-gray-900">
                        {product.name}
                      </p>
                    </div>
                  </div>

                  <ProductReviewForm
                    product={product}
                    compact
                    className="border-0 bg-[#faf9f6]"
                  />
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full"
              onClick={() => setReviewPromptOrder(null)}
            >
              Maybe Later
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
